import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { useToast } from '../context/ToastContext';
import { useNavigate, Link } from 'react-router-dom';

const ProfileScreen = () => {
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading]         = useState(false);
  const navigate                      = useNavigate();
  const { showToast }                 = useToast();
  const user                          = auth.currentUser;

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      const userRef  = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) setDisplayName(userSnap.data().displayName || '');
    };
    fetchData();
  }, [user]);

  const handleUpdateName = async () => {
    if (!displayName.trim()) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), { displayName });
      showToast('השם עודכן בהצלחה! ✓', 'success');
    } catch { showToast('שגיאה בעדכון השם', 'error'); }
    setLoading(false);
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  const handleShareApp = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'המטבח החכם שלך',
          text: 'היי! מצאתי אפליקציה מטורפת שהופכת סרטוני אינסטגרם וטיקטוק למתכונים כתובים בשניות. שווה לנסות!',
          url: 'https://cookswithai.com',
        });
      } catch {}
    } else {
      try {
        await navigator.clipboard.writeText('https://cookswithai.com');
        showToast('הלינק הועתק! שלח אותו לחברים 🔗', 'success');
      } catch { showToast('שגיאה בהעתקת הלינק', 'error'); }
    }
  };

  return (
    <div className="min-h-screen bg-background text-on-surface font-body pb-32" dir="rtl">

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 bg-[#f9f9f9]/80 backdrop-blur-md px-6 py-4 flex justify-between items-center border-b border-outline-variant/10">
        <span className="material-symbols-outlined text-primary text-2xl">restaurant_menu</span>
        <h1 className="font-headline font-bold text-xl text-primary">הפרופיל שלי</h1>
        <div className="w-8" />
      </header>

      <main className="max-w-xl mx-auto px-6 pt-8">

        {/* ── Profile Hero ── */}
        <section className="flex flex-col items-center text-center mb-10">
          <div className="relative mb-5">
            <div className="w-28 h-28 rounded-full p-1 bg-gradient-to-br from-primary to-primary-container shadow-xl">
              <img
                alt="פרופיל"
                className="w-full h-full rounded-full object-cover border-4 border-surface"
                src={user?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName || 'User')}&background=006a62&color=fff&size=200`}
              />
            </div>
          </div>
          <h2 className="text-2xl font-headline font-extrabold tracking-tight mb-0.5">{displayName || 'שף'}</h2>
          <p className="text-on-surface-variant text-sm font-medium mb-6">{user?.email}</p>
        </section>

        {/* ── Edit Name ── */}
        <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 p-6 mb-6">
          <h3 className="text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-4">עריכת פרופיל</h3>
          <div className="flex w-full items-center gap-3">
            <input
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="הכנס את שמך..."
              maxLength={15}
              className="flex-1 w-full min-w-0 bg-surface-container-low border-none outline-none ring-1 ring-outline-variant/30 focus:ring-4 focus:ring-primary/15 rounded-full px-5 py-3 text-on-surface placeholder:text-outline transition-all"
            />
            <button
              onClick={handleUpdateName}
              disabled={loading}
              className="px-6 py-3 flex-shrink-0 whitespace-nowrap bg-primary text-white rounded-full font-label font-bold text-sm shadow-md active:scale-95 transition-all"
            >
              {loading ? '...' : 'שמור'}
            </button>
          </div>
        </div>

        {/* ── Share App ── */}
        <section className="mb-6">
          <button
            onClick={handleShareApp}
            className="w-full bg-gradient-to-br from-primary to-primary-container p-6 rounded-2xl shadow-lg shadow-primary/20 relative overflow-hidden group active:scale-[0.98] transition-all text-right"
          >
            <div className="relative z-10 flex justify-between items-center">
              <div className="max-w-[75%]">
                <h3 className="text-white font-headline font-bold text-lg mb-1">שתף עם חברים</h3>
                <p className="text-white/80 text-xs leading-relaxed">אוהב את האפליקציה? שלח אותה לחברים שגם הם יהפכו לשפים!</p>
              </div>
              <div className="bg-white/20 p-3 rounded-xl backdrop-blur-md">
                <span className="material-symbols-outlined text-white text-2xl">share</span>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
          </button>
        </section>

        {/* ── Settings List ── */}
        <section className="space-y-2 mb-8">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-on-surface-variant px-2 mb-3">מידע כללי</h3>
          <div className="bg-surface-container-lowest rounded-xl overflow-hidden shadow-sm border border-outline-variant/10">
            {[
              { icon: 'privacy_tip', label: 'מדיניות פרטיות', to: '/privacy' },
              { icon: 'description', label: 'תנאי שימוש',      to: '/terms'   },
            ].map(item => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center justify-between p-5 hover:bg-surface-container transition-colors group border-b border-outline-variant/10 last:border-0"
              >
                <div className="flex items-center gap-4">
                  <span className="material-symbols-outlined text-primary text-xl">{item.icon}</span>
                  <span className="font-semibold text-on-surface">{item.label}</span>
                </div>
                <span className="material-symbols-outlined text-outline-variant group-hover:-translate-x-1 transition-transform">chevron_left</span>
              </Link>
            ))}
          </div>
        </section>

        {/* ── Admin Panel — רק למנהל ── */}
        {user?.uid === '2FE2mdzEuQTbNcpgn9Uxq74Aeiv2' && (
          <section className="mb-4">
            <button
              onClick={() => navigate('/admin-panel')}
              className="w-full flex items-center justify-between p-5 bg-surface-container-lowest rounded-xl shadow-sm border border-primary/20 hover:bg-surface-container transition-colors active:scale-[0.98] group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary text-xl">shield</span>
                </div>
                <div className="text-right">
                  <div className="font-bold text-on-surface text-sm">Admin Dashboard</div>
                  <div className="text-xs text-on-surface-variant">ניטור שגיאות, לוגים ומשתמשים</div>
                </div>
              </div>
              <span className="material-symbols-outlined text-primary group-hover:-translate-x-1 transition-transform">chevron_left</span>
            </button>
          </section>
        )}

        {/* ── Logout ── */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-error/10 hover:bg-error/20 text-error font-label font-bold transition-colors active:scale-95 mb-10"
        >
          <span className="material-symbols-outlined">logout</span>
          התנתקות מהחשבון
        </button>

      </main>
    </div>
  );
};

export default ProfileScreen;